import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  forwardRef,
  importProvidersFrom,
  inject,
  Injector,
  provideAppInitializer,
  type ApplicationConfig
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withPreloading, withRouterConfig } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { withLeadingSlash, withTrailingSlash } from 'path-slashes';
import { distinctUntilChanged, first, tap, type Observable } from 'rxjs';

import { OWCC_APP_DESCRIPTION, OWCC_APP_HASH, OWCC_APP_NAME, OWCC_APP_VERSION } from 'src/build-env';

import { ApiConfiguration as OcapiApiConfiguration } from './api/ocapi/api-configuration';
import { ApiConfiguration as OwccApiConfiguration } from './api/owcc/api-configuration';
import { ApiConfiguration as VehicleDeeplinksApiConfiguration } from './api/vehicle-deeplinks/api-configuration';
import { getBaseHref } from './app-base-href';
import { setAppInjector } from './app-injector';
import { AppErrorModule } from './core/app-error/app-error.module';
import { AppErrorEffects } from './core/app-error/store/app-error.effects';
import { CURRENT_HOST_ID } from './core/host-identification/current-host-id';
import { HostId } from './core/host-identification/host-id.enum';
import { LoggerService } from './core/services/logger/logger.service';
import { SettingsService } from './core/services/settings/settings.service';
import { ConfigurationTrackingEffects } from './features/configuration/store/configuration-tracking.effects';
import * as ConfigurationEffects from './features/configuration/store/configuration.effects';
import { MotorizationTrackingEffects } from './features/motorization/store/motorization-tracking.effects';
import { MotorizationEffects } from './features/motorization/store/motorization.effects';
import { BackendVersionInterceptor } from './interceptor/backend-version.interceptor';
import { FeasibilityRequestPendingInterceptor } from './interceptor/feasibility-request-pending.interceptor';
import { HttpRetryInterceptor } from './interceptor/http-retry.interceptor';
import { HttpTimeoutInterceptor } from './interceptor/http-timeout.interceptor';
import { KillswitchInterceptor } from './interceptor/killswitch.interceptor';
import { OcapiApiHeaderInjectionInterceptor } from './interceptor/ocapi-api-header-injection.interceptor';
import { OnlineCodeInterceptor } from './interceptor/online-code.interceptor';
import { OwccApiHeaderInjectionInterceptor } from './interceptor/owcc-api-header-injection.interceptor';
import { AppRoutePreloadingStrategy } from './routing/app-route-preloading-strategy';
import { routes } from './routing/app-routing.module';
import { RccRedirectModule } from './routing/rcc-redirect/rcc-redirect-routing.module';
import { DealerLocatorEffects } from './shared/dealer-locator/store/dealer-locator.effects';
import { metaReducers, reducers } from './store/app.reducer';
import { initApp } from './store/common/common.actions';
import * as CommonEffects from './store/common/common.effects';
import * as SalesTouchEffects from './store/mbcc/effects/sales-touch.effects';
import * as ScrollingEffects from './store/mbcc/effects/scrolling.effects';
import { RouterStoreModule } from './store/router/router-store.module';
import * as RouterEffects from './store/router/router.effects';
import { selectBackendUrl } from './store/runtime-variables/runtime-variables.selectors';
import { UserConfigEffects } from './store/user-config/user-config.effects';
import { filterTruthy } from './utils/functions/rx-filters';
import { WINDOW } from './utils/window.token';

// TODO: Fix with MBCC-2545
// Store<any> should not be necessary (in theory). I have no idea what the correct type should be.
// eslint-disable-next-line @ngrx/no-typed-global-store, @typescript-eslint/no-explicit-any
function initAppFactory(injector: Injector, store: Store<any>): () => Promise<void> {
  return async () =>
    Promise.resolve().then(() => {
      // we save the injector to later make use of it (e.g. to dynamically get instance of the store in a pure function)
      setAppInjector(injector);

      store.dispatch(initApp());

      const loggerService = injector.get(LoggerService);
      loggerService.info(OWCC_APP_DESCRIPTION, `Version: ${OWCC_APP_VERSION} (Commit: ${OWCC_APP_HASH})`);
      loggerService.info(`Host/Env ID: ${CURRENT_HOST_ID}`);

      const win = injector.get<Window>(WINDOW);
      win.owcc = win.owcc || {};

      win.owcc.version = OWCC_APP_VERSION;
      win.owcc.commit = OWCC_APP_HASH;

      win.owcc.assetUri = withTrailingSlash(win.owcc.assetUri ?? OWCC_APP_NAME);
      if (!/^https?:/.test(win.owcc.assetUri)) {
        win.owcc.assetUri = withLeadingSlash(win.owcc.assetUri);
      }
    });
}

function initializeApiConfigFactory(
  // TODO: Fix with MBCC-2545
  // Store<any> should not be necessary (in theory). I have no idea what the correct type should be.
  // eslint-disable-next-line @ngrx/no-typed-global-store, @typescript-eslint/no-explicit-any
  store: Store<any>,
  settingsService: SettingsService,
  owccApiConfiguration: OwccApiConfiguration,
  vehicleDeeplinksApiConfiguration: VehicleDeeplinksApiConfiguration,
  ocapiApiConfiguration: OcapiApiConfiguration
): () => Observable<string> {
  return () => {
    const applyOwccApiConfig = (backendUrl: string): void => {
      owccApiConfiguration.rootUrl = backendUrl.split('/api')[0];
    };

    const applyDeeplinksApiConfig = (): void => {
      vehicleDeeplinksApiConfiguration.rootUrl = settingsService.data.apis.deeplinks.baseUrl || '';
    };

    const applyOcapiApiConfig = (): void => {
      ocapiApiConfiguration.rootUrl = settingsService.data.apis.ocapi.baseUrl || '';
    };

    return store.select(selectBackendUrl).pipe(
      filterTruthy(),
      first(), // we need to complete the observable for the app initializer to work!
      tap(backendUrl => {
        applyOwccApiConfig(backendUrl || '');
      }),
      tap(() => {
        applyDeeplinksApiConfig();
      }),
      tap(() => {
        applyOcapiApiConfig();
      }),
      tap(() => {
        // additionally we again listen for the whole application lifecycle for a config change (needed e.g. for doorway page changes)
        store
          .select(selectBackendUrl)
          .pipe(filterTruthy(), distinctUntilChanged())
          .subscribe(backendUrl => {
            applyOwccApiConfig(backendUrl || '');
          });
      })
    ) as unknown as Observable<string>;
  };
}

export const exposedForTesting = { initializeApiConfigFactory, initAppFactory };

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withPreloading(AppRoutePreloadingStrategy),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        // scrollPositionRestoration intentionally disabled since treated in router.effects.ts
        // also see documentation here: https://angular.io/api/router/ExtraOptions#scrollPositionRestoration
        scrollPositionRestoration: 'disabled'
      }),
      withRouterConfig({
        urlUpdateStrategy: 'eager',
        paramsInheritanceStrategy: 'always'
      })
    ),
    provideStore(reducers, { metaReducers }),
    provideRouterStore(),
    provideEffects([
      RouterEffects,
      CommonEffects,
      ConfigurationEffects,
      ConfigurationTrackingEffects,
      MotorizationEffects,
      MotorizationTrackingEffects,
      AppErrorEffects,
      SalesTouchEffects,
      ScrollingEffects,
      UserConfigEffects,
      DealerLocatorEffects
    ]),
    provideStoreDevtools({
      // eslint-disable-next-line no-restricted-globals
      name: `${OWCC_APP_NAME.toUpperCase()} Store ${CURRENT_HOST_ID} (${window.location.host} ${OWCC_APP_VERSION} ${OWCC_APP_HASH})`,
      maxAge: 100,
      logOnly: CURRENT_HOST_ID === HostId.Prod
    }),

    importProvidersFrom(
      // import of the RouterStoreModule needed in order to dispatch the corresponding (see RouterEvent)
      // - at least as long as we are not fully migrated to the currently called `Next Router` (router store)
      RouterStoreModule,
      RccRedirectModule,

      AppErrorModule
    ),
    // dynamic BaseHref provider
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseHref,
      deps: [PlatformLocation, WINDOW]
    },
    provideAppInitializer(async () => {
      const initializerFn = initAppFactory(inject(Injector), inject(Store));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeApiConfigFactory(
        inject(Store),
        inject(SettingsService),
        inject(OwccApiConfiguration),
        inject(VehicleDeeplinksApiConfiguration),
        inject(OcapiApiConfiguration)
      );
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KillswitchInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OnlineCodeInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BackendVersionInterceptor,
      multi: true
    },
    OwccApiHeaderInjectionInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      // TODO: Fix with MBCC-2651
      // investigate how/if this can be solved withour forwardRef
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => OwccApiHeaderInjectionInterceptor),
      multi: true
    },
    OcapiApiHeaderInjectionInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      // TODO: Fix with MBCC-2651
      // investigate how/if this can be solved withour forwardRef
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => OcapiApiHeaderInjectionInterceptor),
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRetryInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpTimeoutInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FeasibilityRequestPendingInterceptor,
      multi: true
    },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi())
  ]
};
